import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { customerAPI } from '../services/api';
import db from '../db/localdb';
import { useAuth } from '../context/AuthContext';
import RatingBadge from '../components/RatingBadge';
import BackButton from '../components/BackButton';

export default function Customers() {
  const { isEnterprise, user, business } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [search,    setSearch]    = useState('');
  const [loading,   setLoading]   = useState(true);
  const [bizFilter, setBizFilter] = useState('');   // '' = all businesses

  // Derive unique business names for filter pills (owner only)
  const bizNames = useMemo(() => {
    const names = [...new Set(customers.map(c => c.business_name).filter(Boolean))];
    return names.sort();
  }, [customers]);

  const showBizBadge = isEnterprise && user?.role === 'owner' && bizNames.length > 1;

  useEffect(() => {
    const t = setTimeout(() => {
      customerAPI.list({ search: search || undefined })
        .then(r => setCustomers(r.data.customers || []))
        .catch(async () => {
          // Offline: fall back to the local Dexie mirror so customers (including
          // ones just created while offline) still show and are searchable.
          const bid = business?.id;
          let rows = await db.customers.filter(c => !c.voided_at).toArray();
          if (bid) rows = rows.filter(c => !c.business_id || c.business_id === bid);
          const q = (search || '').trim().toLowerCase();
          if (q) rows = rows.filter(c =>
            (c.name || '').toLowerCase().includes(q) ||
            (c.contact || '').toLowerCase().includes(q));
          rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setCustomers(rows);
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search, business?.id]);

  return (
    <div className="page">
      <BackButton />
      <h1 className="page-title">Customers</h1>
      <div className="form-group">
        <input placeholder="Search by name or contact…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Business filter pills — only when owner has multiple businesses */}
      {showBizBadge && (
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <button
            onClick={() => setBizFilter('')}
            style={{ padding: '0.25rem 0.7rem', borderRadius: '999px', border: 'none', cursor: 'pointer', fontSize: '0.78rem',
              background: bizFilter === '' ? 'var(--color-primary)' : 'var(--color-card, #1e293b)',
              color: bizFilter === '' ? '#fff' : 'var(--color-muted)', fontWeight: bizFilter === '' ? 700 : 400 }}
          >
            All
          </button>
          {bizNames.map(name => (
            <button key={name} onClick={() => setBizFilter(name)}
              style={{ padding: '0.25rem 0.7rem', borderRadius: '999px', border: 'none', cursor: 'pointer', fontSize: '0.78rem',
                background: bizFilter === name ? 'var(--color-primary)' : 'var(--color-card, #1e293b)',
                color: bizFilter === name ? '#fff' : 'var(--color-muted)', fontWeight: bizFilter === name ? 700 : 400 }}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      {!loading && customers.length === 0 && <p className="text-muted">No customers found.</p>}

      {customers
        .filter(c => !bizFilter || c.business_name === bizFilter)
        .map(c => (
        <Link key={c.id} to={`/customers/${c.id}`} style={{ display: 'block', textDecoration: 'none', color: 'inherit', marginBottom: '0.5rem' }}>
          <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{c.name}</div>
              {c.contact && <div className="text-muted" style={{ fontSize: '0.85rem' }}>{c.contact}</div>}
              {showBizBadge && c.business_name && (
                <div style={{ marginTop: '0.2rem' }}>
                  <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.45rem', borderRadius: '4px',
                    background: 'var(--color-primary-muted, #312e81)', color: 'var(--color-primary)' }}>
                    {c.business_name}
                  </span>
                </div>
              )}
            </div>
            <RatingBadge rating={c.rating} />
          </div>
        </Link>
      ))}
    </div>
  );
}
